//src/hooks/useClientCompanies.ts
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  createClientCompany,
  getClientCompanyDetail,
  listClientCompanies,
  setClientCompanyActive,
  updateClientCompany,
  type ClientCompanyUpsertIn,
  type ListClientCompaniesParams,
} from "@/services/clientCompanies";

export const CLIENT_COMPANIES_QK = (params: ListClientCompaniesParams) =>
  ["client_companies", params] as const;

function notifyClientCompaniesChanged() {
  window.dispatchEvent(new CustomEvent("normex:client-companies-changed"));
}

export function useClientCompanies(params: ListClientCompaniesParams, enabled = true) {
  return useQuery({
    queryKey: CLIENT_COMPANIES_QK(params),
    queryFn: () => listClientCompanies(params),
    enabled,
    staleTime: 20_000,
  });
}

export function useClientCompanyDetail(clientCompanyId: string | undefined) {
  return useQuery({
    queryKey: ["client_company_detail", clientCompanyId],
    queryFn: () => getClientCompanyDetail(clientCompanyId as string),
    enabled: !!clientCompanyId,
    staleTime: 20_000,
  });
}

export function useCreateClientCompany() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (body: ClientCompanyUpsertIn) => createClientCompany(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["client_companies"] });
      notifyClientCompaniesChanged();
    },
  });
}

export function useUpdateClientCompany() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: ClientCompanyUpsertIn }) =>
      updateClientCompany(id, body),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["client_companies"] });
      qc.invalidateQueries({ queryKey: ["client_company_detail", variables.id] });
      notifyClientCompaniesChanged();
    },
  });
}

export function useSetClientCompanyActive() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) =>
      setClientCompanyActive(id, is_active),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["client_companies"] });
      qc.invalidateQueries({ queryKey: ["client_company_detail", variables.id] });
      notifyClientCompaniesChanged();
    },
  });
}
